/**
 * Radio Ads Frontend Hook & Components.
 *
 *  • `useRadioBanner(station)` — polls /api/radio/ads/banner every
 *    BANNER_REFRESH_SECONDS and returns the current banner (or null).
 *  • `<RadioBannerAd>` — small dismissible card above the waveform.
 *  • `requestNextAd(station, play_count)` — call between tracks; if
 *    the engine returns a paid audio ad, the component plays it and
 *    fires impression/completion events automatically.
 *
 * VIP / PTL listeners get `is_ad_free: true` from the engine so the
 * hook short-circuits and never fetches anything.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { ExternalLink, X, Megaphone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BANNER_REFRESH_MS = 120_000; // 2 min
const STORAGE_DISMISS_KEY = 'ovd:radio-banner-dismissed-until';

export function useRadioBanner(station, token) {
  const [banner, setBanner] = useState(null);
  const [adFree, setAdFree] = useState(false);
  const dismissedUntilRef = useRef(0);

  // Hydrate dismissal state once
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_DISMISS_KEY);
      if (raw) dismissedUntilRef.current = Number(raw) || 0;
    } catch {}
  }, []);

  useEffect(() => {
    if (!station) return;
    let alive = true;
    const fetchBanner = async () => {
      // Honour user's dismissal for 30 minutes
      if (Date.now() < dismissedUntilRef.current) {
        if (alive) setBanner(null);
        return;
      }
      try {
        const r = await axios.get(`${API}/radio/ads/banner?station=${station}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!alive) return;
        if (r.data?.is_ad_free || r.data?.visual_ad_free) {
          setAdFree(true);
          setBanner(null);
          return;
        }
        setAdFree(false);
        setBanner(r.data?.ad || null);
        // Fire impression once we render
        if (r.data?.ad?.id) {
          recordEvent({
            ad_id: r.data.ad.id,
            station,
            placement: 'banner',
            kind: 'impression',
            token,
          });
          // Surface the dual-CTA "Skip ads forever" chip ONLY for
          // non-VIP / non-PTL listeners (audio_ad_free=false).
          try {
            window.dispatchEvent(new CustomEvent('ovd:radio-ad-fired', {
              detail: {
                placement: 'banner',
                station,
                audio_ad_free: !!r.data?.audio_ad_free,
              },
            }));
          } catch {}
        }
      } catch {
        if (alive) setBanner(null);
      }
    };
    fetchBanner();
    const i = setInterval(fetchBanner, BANNER_REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(i);
    };
  }, [station, token]);

  const dismiss = useCallback(() => {
    const until = Date.now() + 30 * 60 * 1000;
    dismissedUntilRef.current = until;
    try {
      localStorage.setItem(STORAGE_DISMISS_KEY, String(until));
    } catch {}
    setBanner(null);
  }, []);

  return { banner, adFree, dismiss };
}

export async function recordEvent({ ad_id, station, placement, kind, token }) {
  if (!ad_id) return;
  try {
    await axios.post(
      `${API}/radio/ads/event`,
      { ad_id, station, placement, kind },
      { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
    );
  } catch {
    /* fire-and-forget */
  }
}

export async function requestNextAd({ station, play_count, placement = 'between_track', token }) {
  try {
    const r = await axios.post(
      `${API}/radio/ads/next`,
      { station, play_count, placement },
      { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
    );
    return r.data;
  } catch {
    return { ad: null, kind: 'skip' };
  }
}

/**
 * Banner ad component. Renders nothing if the listener is ad-free or
 * no banner is currently available. Adopts the active station theme.
 */
export function RadioBannerAd({ station, token, theme }) {
  const { banner, dismiss } = useRadioBanner(station, token);
  if (!banner) return null;
  const onClickAd = () => {
    if (banner.id) recordEvent({ ad_id: banner.id, station, placement: 'banner', kind: 'click', token });
    if (banner.click_url) window.open(banner.click_url, '_blank', 'noopener');
  };
  const accent = theme?.primary || '#FFDA33';
  return (
    <div
      className="mx-5 mt-2 mb-1 rounded-xl overflow-hidden border relative group"
      style={{
        borderColor: `${accent}44`,
        background: `linear-gradient(90deg, ${accent}10, rgba(0,0,0,0.6))`,
      }}
      data-testid="radio-banner-ad"
    >
      <div
        className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-white/[0.03] transition-colors"
        onClick={onClickAd}
      >
        {banner.image_url ? (
          <img
            src={banner.image_url}
            alt=""
            className="w-10 h-10 rounded-md object-cover shrink-0"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div
            className="w-10 h-10 rounded-md flex items-center justify-center shrink-0"
            style={{ background: `${accent}20` }}
          >
            <Megaphone size={18} style={{ color: accent }} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#888]">
            {banner.label || 'Sponsored'}
          </div>
          <div className="text-white text-sm font-bold truncate">
            {banner.title || banner.advertiser || 'Featured'}
          </div>
          {banner.artist && (
            <div className="text-[#aaa] text-xs truncate">{banner.artist}</div>
          )}
        </div>
        <ExternalLink size={14} className="text-[#aaa] shrink-0" />
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); dismiss(); }}
        className="absolute top-1 right-1 text-[#666] hover:text-white p-1 rounded-full hover:bg-white/10"
        aria-label="Dismiss"
        data-testid="radio-banner-ad-dismiss"
      >
        <X size={12} />
      </button>
    </div>
  );
}

export default RadioBannerAd;
